/**
 * Shared helpers for the company trial registration flow
 * (POST admin/ssms/send-start-trail-otp-request, POST admin/ssms/verify-start-trail-otp).
 *
 * The API caches the whole registration form against the email for 10 minutes
 * and "resend" is a re-POST of that form, so the form is kept in sessionStorage
 * (tab-scoped) until verification succeeds or fails terminally.
 */

export const TRIAL_ENDPOINTS = {
  sendOtp: 'admin/ssms/send-start-trail-otp-request',
  verifyOtp: 'admin/ssms/verify-start-trail-otp'
} as const;

/** OTP validity on the server. */
export const OTP_VALIDITY_MINUTES = 10;
/** Provisioning runs synchronously inside the verify call and can take tens of seconds. */
export const VERIFY_TIMEOUT_MS = 120_000;
/** Client-side throttling: the server has no rate limit or lockout today. */
export const RESEND_COOLDOWN_SECONDS = 60;
export const MAX_VERIFY_ATTEMPTS = 5;

export interface TrialSignupForm {
  fullName: string;
  email: string;
  phone: string;
  companyName: string;
  industry: string;
  numberOfEmployees: number;
  password: string;
}

const STORAGE_KEY = 'trialSignup';

export function saveTrialSignup(form: TrialSignupForm): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(form));
}

export function loadTrialSignup(): TrialSignupForm | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as TrialSignupForm) : null;
  } catch {
    return null;
  }
}

export function clearTrialSignup(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

/** Request body for send-start-trail-otp-request, exactly as the API expects it. */
export function toTrialRequest(form: TrialSignupForm) {
  return {
    companyName: form.companyName.trim(),
    companyField: form.industry,
    numberOfEmployees: Number(form.numberOfEmployees),
    contactPersonName: form.fullName.trim(),
    contactPersonMobile: form.phone.trim(),
    contactPersonEmail: form.email.trim(),
    contactPersonPassword: form.password
  };
}

/**
 * The server derives the company code from the first letter of each word of
 * the company name, uppercased ("Makaya Legal Services" -> "MLS").
 */
export function companyCodeFor(companyName: string): string {
  return (companyName || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0].toUpperCase())
    .join('');
}

/**
 * Pulls a human-readable message out of the API's inconsistent error shapes:
 * a plain string, an array of strings, `{ message }`, or `{ errors }`.
 */
export function extractApiMessage(error: any, fallback: string): string {
  const body = error?.error ?? error;
  if (typeof body === 'string' && body.trim()) {
    return body.trim();
  }
  if (Array.isArray(body) && body.length) {
    return String(body[0]);
  }
  if (body && typeof body === 'object') {
    if (typeof body.message === 'string' && body.message.trim()) {
      return body.message.trim();
    }
    if (body.errors) {
      const first = Array.isArray(body.errors) ? body.errors[0] : Object.values(body.errors).flat()[0];
      if (first) {
        return String(first);
      }
    }
    if (typeof body.title === 'string') {
      return body.title;
    }
  }
  return fallback;
}

/** What the OTP screen should offer after a failed verification. */
export type VerifyOutcome =
  | 'resend' // OTP expired / cache lost / email mismatch -> request a new code
  | 'retry' // wrong code -> let them retype (attempts capped client-side)
  | 'login' // email already owns a company -> sign in at the main app
  | 'rename' // company initials collide or name invalid -> change the company name
  | 'timeout' // the call outran our timeout; provisioning may still finish
  | 'support'; // infrastructure failure mid-provision -> do not retry, contact support

export function classifyVerifyFailure(message: string): VerifyOutcome {
  const m = (message || '').toLowerCase();
  if (m.includes('company email already exist')) {
    return 'login';
  }
  if (m.includes('company already exist') || m.includes('invalid company name')) {
    return 'rename';
  }
  if (m.includes('expired') || m.includes('invalid email') || m.includes('email and otp are required')) {
    return 'resend';
  }
  if (m.includes('invalid otp')) {
    return 'retry';
  }
  return 'support';
}
